import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';
import { C, alpha } from '../lib/theme.js';
import { Button } from '../ui/ui.js';
import { Icon } from '../ui/icons.js';
import type {
  AdInterest,
  AudienceBucket,
  AudienceInterpretation,
  AudienceSpec,
  GeoResult,
  RegionCity,
} from '../lib/types.js';

/**
 * Placeholder examples, PER TENANT. These used to be hardcoded Phuket Cleanse
 * ("cold plunges, longevity, Sydney") and rendered for every workspace, so a
 * Propty operator opened the audience editor and was prompted with a wellness
 * retreat's targeting. Cosmetic rather than a data leak, but it reads as one,
 * and it is exactly the cross-tenant bleed the product must not show.
 *
 * An unknown slug gets the NEUTRAL set, never another tenant's — so onboarding a
 * third client can't leak either of the first two.
 */
interface AudienceExamples {
  intent: string;
  name: string;
  interest: string;
  empty: string;
}
const NEUTRAL_EXAMPLES: AudienceExamples = {
  intent: 'e.g. People in London and Manchester interested in home renovation, 30+',
  name: 'e.g. London renovators',
  interest: 'Search interests to add…',
  empty:
    'No audiences yet. Describe one in plain English and Basil builds the Meta targeting for you to reuse at launch.',
};
const EXAMPLES_BY_SLUG: Record<string, AudienceExamples> = {
  'phuket-cleanse': {
    intent: 'e.g. People in Sydney and Melbourne into cold plunges, longevity and biohacking, 35+',
    name: 'e.g. Sydney cold-plunge crowd',
    interest: 'Search interests to add… (e.g. Cold exposure)',
    empty:
      'No audiences yet. Describe one in plain English (e.g. “people into cold plunges and longevity in Sydney”) and Basil builds the Meta targeting for you to reuse at launch.',
  },
  propty: {
    intent: 'e.g. UK property investors into buy-to-let and property development, 28 to 60',
    name: 'e.g. UK buy-to-let investors',
    interest: 'Search interests to add… (e.g. Buy to let)',
    empty:
      'No audiences yet. Describe one in plain English (e.g. “UK property investors into buy-to-let”) and Basil builds the Meta targeting for you to reuse at launch.',
  },
};
function examplesFor(slug: string | undefined): AudienceExamples {
  return (slug && EXAMPLES_BY_SLUG[slug]) || NEUTRAL_EXAMPLES;
}

/**
 * Audience buckets manager — the operator describes an audience in plain English,
 * Basil turns it into a real Meta targeting spec (age/gender/geo/interests), and
 * it's saved as a reusable bucket any Boost or new-ad launch can select. The
 * locked standard stays the default; buckets are opt-in overrides.
 */
export function AudienceBucketsManager({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const { activeOrg } = useOrg();
  const ex = examplesFor(activeOrg?.slug);
  const [items, setItems] = useState<AudienceBucket[]>([]);
  const [editing, setEditing] = useState<AudienceBucket | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    apiFetch<{ buckets: AudienceBucket[] }>(`/api/orgs/${orgId}/audience-buckets`)
      .then((d) => setItems(d.buckets))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [orgId]);
  useEffect(() => load(), [load]);

  async function remove(id: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/audience-buckets/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <div>
      {error && <div style={{ color: C.act, fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      {items.length === 0 && editing !== 'new' && (
        <div style={{ fontSize: 12.5, color: C.faint, marginBottom: 12 }}>{ex.empty}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((t) =>
          editing !== null && editing !== 'new' && editing.id === t.id ? (
            <AudienceEditor
              key={t.id}
              orgId={orgId}
              initial={t}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />
          ) : (
            <div
              key={t.id}
              style={{
                border: `1px solid ${C.line}`,
                borderRadius: 10,
                padding: '12px 14px',
                background: C.surface,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: C.primary }}>{t.name}</div>
                <AudienceSummary spec={t.spec} />
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 12, fontSize: 11.5, flexShrink: 0 }}>
                  <span
                    onClick={() => setEditing(t)}
                    style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Edit
                  </span>
                  <span
                    onClick={() => void remove(t.id)}
                    style={{ color: C.act, cursor: 'pointer' }}
                  >
                    Delete
                  </span>
                </div>
              )}
            </div>
          ),
        )}
      </div>

      {editing === 'new' ? (
        <div style={{ marginTop: 8 }}>
          <AudienceEditor
            orgId={orgId}
            initial={null}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        </div>
      ) : (
        canEdit && (
          <Button onClick={() => setEditing('new')} style={{ marginTop: 12 }}>
            + New audience
          </Button>
        )
      )}
    </div>
  );
}

/** One-line human summary of a bucket's targeting (age · gender · places · interests). */
export function AudienceSummary({ spec }: { spec: AudienceSpec }) {
  const bits: string[] = [];
  const ageMin = spec.ageMin ?? null;
  const ageMax = spec.ageMax ?? null;
  if (ageMin && ageMax) bits.push(`Age ${ageMin}–${ageMax}`);
  else if (ageMin) bits.push(`Age ${ageMin}+`);
  else if (ageMax) bits.push(`Up to ${ageMax}`);
  if (spec.genders && spec.genders !== 'all') bits.push(spec.genders === 'men' ? 'Men' : 'Women');
  const places = [...(spec.cities ?? []), ...(spec.countries ?? [])];
  return (
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.6 }}>
      {bits.length > 0 && <div>{bits.join(' · ')}</div>}
      {places.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
          {places.map((p) => (
            <Chip key={p.key} label={p.name} tone={C.info} />
          ))}
        </div>
      )}
      {(spec.customAudiences ?? []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
          {(spec.customAudiences ?? []).map((ca) => (
            <Chip key={ca.id} label={`✦ ${ca.name}`} tone={C.magic} />
          ))}
        </div>
      )}
      {(spec.interests ?? []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
          {(spec.interests ?? []).map((i) => (
            <Chip key={i.id} label={i.name} tone={C.basil} />
          ))}
        </div>
      )}
      {places.length === 0 &&
        (spec.interests ?? []).length === 0 &&
        (spec.customAudiences ?? []).length === 0 &&
        bits.length === 0 && <span style={{ color: C.faint }}>Empty audience</span>}
    </div>
  );
}

function Chip({ label, tone, onRemove }: { label: string; tone: string; onRemove?: () => void }) {
  return (
    <span
      style={{
        font: `500 10.5px 'IBM Plex Mono',monospace`,
        color: tone,
        background: alpha(tone, 12),
        borderRadius: 5,
        padding: '2px 8px',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {label}
      {onRemove && (
        <span onClick={onRemove} style={{ marginLeft: 6, cursor: 'pointer', opacity: 0.7 }}>
          ✕
        </span>
      )}
    </span>
  );
}

/** Create/edit an audience — intent box + Interpret, then editable spec. */
function AudienceEditor({
  orgId,
  initial,
  onCancel,
  onSaved,
}: {
  orgId: string;
  initial: AudienceBucket | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { activeOrg } = useOrg();
  const ex = examplesFor(activeOrg?.slug);
  const [name, setName] = useState(initial?.name ?? '');
  const [intent, setIntent] = useState(initial?.intent ?? '');
  const [ageMin, setAgeMin] = useState<string>(initial?.spec.ageMin?.toString() ?? '');
  const [ageMax, setAgeMax] = useState<string>(initial?.spec.ageMax?.toString() ?? '');
  const [genders, setGenders] = useState<'all' | 'men' | 'women'>(initial?.spec.genders ?? 'all');
  const [cities, setCities] = useState<RegionCity[]>(initial?.spec.cities ?? []);
  const [countries, setCountries] = useState<RegionCity[]>(initial?.spec.countries ?? []);
  const [interests, setInterests] = useState<AdInterest[]>(initial?.spec.interests ?? []);
  const [rationale, setRationale] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [interpreting, setInterpreting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function interpret() {
    if (intent.trim().length < 3 || interpreting) return;
    setInterpreting(true);
    setError(null);
    try {
      const d = await apiFetch<AudienceInterpretation>(`/api/orgs/${orgId}/audience/interpret`, {
        method: 'POST',
        body: JSON.stringify({ intent: intent.trim() }),
      });
      setAgeMin(d.spec.ageMin?.toString() ?? '');
      setAgeMax(d.spec.ageMax?.toString() ?? '');
      setGenders(d.spec.genders ?? 'all');
      setCities(d.spec.cities ?? []);
      setCountries(d.spec.countries ?? []);
      setInterests(d.spec.interests ?? []);
      setRationale(d.rationale ?? '');
      setWarnings(d.warnings ?? []);
      if (!name.trim()) setName(intent.trim().slice(0, 40));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not interpret that.');
    } finally {
      setInterpreting(false);
    }
  }

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    const spec: AudienceSpec = {
      ageMin: ageMin ? Number(ageMin) : null,
      ageMax: ageMax ? Number(ageMax) : null,
      genders,
      cities,
      countries,
      interests,
    };
    try {
      const body = JSON.stringify({ name: name.trim(), intent: intent.trim(), spec });
      if (initial) {
        await apiFetch(`/api/orgs/${orgId}/audience-buckets/${initial.id}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await apiFetch(`/api/orgs/${orgId}/audience-buckets`, { method: 'POST', body });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setBusy(false);
    }
  }

  const hasGeo = cities.length > 0 || countries.length > 0;
  const canSave = !!name.trim() && hasGeo && !busy;

  return (
    <div
      style={{
        border: `1px solid ${C.basil}`,
        borderRadius: 10,
        padding: 14,
        background: C.surface,
      }}
    >
      {error && <div style={{ color: C.act, fontSize: 12, marginBottom: 8 }}>{error}</div>}

      <div className="label-mono" style={{ marginBottom: 6 }}>
        Who&apos;s this for? (plain English)
      </div>
      <textarea
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        placeholder={ex.intent}
        rows={2}
        style={{ ...field, marginBottom: 8, resize: 'vertical' }}
      />
      <Button
        onClick={() => void interpret()}
        style={{
          marginBottom: 12,
          background: alpha(C.basil, 12),
          color: C.basil,
          borderColor: alpha(C.basil, 40),
          opacity: intent.trim().length < 3 || interpreting ? 0.55 : 1,
        }}
      >
        {interpreting ? (
          'Thinking…'
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="sparkles" size={14} />
            Interpret with Basil
          </span>
        )}
      </Button>

      {rationale && (
        <div
          style={{
            fontSize: 12,
            color: C.secondary,
            background: alpha(C.info, 8),
            border: `1px solid ${alpha(C.info, 25)}`,
            borderRadius: 8,
            padding: '9px 11px',
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          {rationale}
        </div>
      )}
      {warnings.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {warnings.map((w, i) => (
            <div
              key={i}
              style={{
                fontSize: 11.5,
                color: C.watch,
                lineHeight: 1.5,
                display: 'flex',
                gap: 6,
              }}
            >
              <Icon name="alert" size={13} style={{ marginTop: 1 }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      <div className="label-mono" style={{ marginBottom: 6 }}>
        Name
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={ex.name}
        style={{ ...field, marginBottom: 12 }}
      />

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="label-mono" style={{ marginBottom: 6 }}>
            Age min
          </div>
          <input
            value={ageMin}
            onChange={(e) => setAgeMin(e.target.value.replace(/\D/g, ''))}
            placeholder="—"
            style={{ ...field, width: 80 }}
          />
        </div>
        <div>
          <div className="label-mono" style={{ marginBottom: 6 }}>
            Age max
          </div>
          <input
            value={ageMax}
            onChange={(e) => setAgeMax(e.target.value.replace(/\D/g, ''))}
            placeholder="—"
            style={{ ...field, width: 80 }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div className="label-mono" style={{ marginBottom: 6 }}>
            Gender
          </div>
          <select
            value={genders}
            onChange={(e) => setGenders(e.target.value as 'all' | 'men' | 'women')}
            style={field}
          >
            <option value="all">All</option>
            <option value="men">Men</option>
            <option value="women">Women</option>
          </select>
        </div>
      </div>

      <div className="label-mono" style={{ marginBottom: 6 }}>
        Interests
      </div>
      <InterestPicker
        orgId={orgId}
        interests={interests}
        setInterests={setInterests}
        field={field}
      />

      <div className="label-mono" style={{ margin: '12px 0 6px' }}>
        Target cities
      </div>
      <CityPicker orgId={orgId} cities={cities} setCities={setCities} field={field} />
      {countries.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {countries.map((c) => (
            <Chip
              key={c.key}
              label={`${c.name} (country)`}
              tone={C.info}
              onRemove={() => setCountries((cs) => cs.filter((x) => x.key !== c.key))}
            />
          ))}
        </div>
      )}

      {!hasGeo && (
        <div style={{ fontSize: 11.5, color: C.faint, marginTop: 8 }}>
          Add at least one city or country so this audience is ready to launch.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => canSave && void save()}
          style={{ opacity: canSave ? 1 : 0.55 }}
        >
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Create audience'}
        </Button>
      </div>
    </div>
  );
}

/** Interest typeahead (Meta interest-search) + removable chips. */
function InterestPicker({
  orgId,
  interests,
  setInterests,
  field,
}: {
  orgId: string;
  interests: AdInterest[];
  setInterests: React.Dispatch<React.SetStateAction<AdInterest[]>>;
  field: React.CSSProperties;
}) {
  const { activeOrg } = useOrg();
  const ex = examplesFor(activeOrg?.slug);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AdInterest[]>([]);
  const [searching, setSearching] = useState(false);
  const seen = useRef(new Set(interests.map((i) => i.id)));
  seen.current = new Set(interests.map((i) => i.id));

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      apiFetch<{ results: AdInterest[] }>(
        `/api/orgs/${orgId}/meta/interest-search?q=${encodeURIComponent(term)}`,
      )
        .then((d) => setResults(d.results))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, orgId]);

  const add = (i: AdInterest) => {
    if (seen.current.has(i.id)) return;
    setInterests((cur) => [...cur, i]);
    setQ('');
    setResults([]);
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={ex.interest}
          style={field}
        />
        {q.trim().length >= 2 && (
          <div style={dropdown}>
            {searching && results.length === 0 ? (
              <div style={dropItemMuted}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={dropItemMuted}>No matching interests.</div>
            ) : (
              results.map((i) => (
                <div
                  key={i.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(i);
                  }}
                  style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, color: C.primary }}
                >
                  {i.name}
                  {i.audience_size ? (
                    <span style={{ color: C.faint, fontSize: 11 }}>
                      {' '}
                      · {abbrev(i.audience_size)}
                    </span>
                  ) : null}
                  {i.path && i.path.length > 1 ? (
                    <span style={{ color: C.faint, fontSize: 10.5 }}> · {i.path[0]}</span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {interests.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {interests.map((i) => (
            <Chip
              key={i.id}
              label={i.name}
              tone={C.basil}
              onRemove={() => setInterests((cur) => cur.filter((x) => x.id !== i.id))}
            />
          ))}
        </div>
      )}
    </>
  );
}

/** City typeahead (Meta geo-search) + removable chips. */
function CityPicker({
  orgId,
  cities,
  setCities,
  field,
}: {
  orgId: string;
  cities: RegionCity[];
  setCities: React.Dispatch<React.SetStateAction<RegionCity[]>>;
  field: React.CSSProperties;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<GeoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const seen = useRef(new Set(cities.map((c) => c.key)));
  seen.current = new Set(cities.map((c) => c.key));

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      apiFetch<{ results: GeoResult[] }>(
        `/api/orgs/${orgId}/meta/geo-search?q=${encodeURIComponent(term)}`,
      )
        .then((d) => setResults(d.results))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, orgId]);

  const add = (g: GeoResult) => {
    if (seen.current.has(g.key)) return;
    setCities((cs) => [...cs, { key: g.key, name: g.name }]);
    setQ('');
    setResults([]);
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a city to add…"
          style={field}
        />
        {q.trim().length >= 2 && (
          <div style={dropdown}>
            {searching && results.length === 0 ? (
              <div style={dropItemMuted}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={dropItemMuted}>No matches.</div>
            ) : (
              results.map((g) => (
                <div
                  key={g.key}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(g);
                  }}
                  style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, color: C.primary }}
                >
                  {g.name}
                  {g.region ? `, ${g.region}` : ''}
                  {g.country_name ? (
                    <span style={{ color: C.faint }}> · {g.country_name}</span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {cities.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {cities.map((c) => (
            <Chip
              key={c.key}
              label={c.name}
              tone={C.basil}
              onRemove={() => setCities((cs) => cs.filter((x) => x.key !== c.key))}
            />
          ))}
        </div>
      )}
    </>
  );
}

function abbrev(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

const field: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: C.surface2,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '8px 11px',
  fontSize: 13,
  color: C.primary,
  fontFamily: 'inherit',
};
const dropdown: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 30,
  background: C.surface,
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  overflow: 'hidden',
  maxHeight: 220,
  overflowY: 'auto',
  boxShadow: '0 12px 32px rgba(0,0,0,.4)',
};
const dropItemMuted: React.CSSProperties = { padding: '9px 12px', color: C.muted, fontSize: 12.5 };
